import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Empty, Modal, message } from 'antd'
import axios from 'axios'
import filter from 'lodash/filter'
import fromPairs from 'lodash/fromPairs'
import get from 'lodash/get'
import includes from 'lodash/includes'
import isEmpty from 'lodash/isEmpty'
import isNil from 'lodash/isNil'
import map from 'lodash/map'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
//import { getCompanyId } from '../selectors/auth';
import { fetchCompanyIfNeeded } from '../actions/company'
import { setPageTitle } from '../actions/title'
import DealProfileContainer from '../components/DealProfileContainer/DealProfileContainer'
import DealWizard from '../components/DealWizard/DealWizard'
import { getInitialDealsValues } from '../components/DynamicForm/DynamicForm'
import LoadingGif from '../components/LoadingGif'
import { ServiceProviderValidatorShape } from '../components/ServiceProviderWizard/ServiceProviderValidatorShape'
import * as types from '../constants/types/serviceprovideronboarding'
import http from '../utils/api'
import useDealsParsing from '../utils/hooks/useDealsParsing'
import { dealsAnswers } from '../utils/wizardContainer'

const converters = {
  'Numeric - INT': (n) => {
    if (isNil(n)) {
      return null
    }
    return parseInt(n, 10)
  },
  'Numeric - FLOAT': (n) => {
    if (isNil(n)) {
      return null
    }
    return parseFloat(n)
  },
  'Boolean': (b) => {
    if (isNil(b)) {
      return null
    }
    return b === 'true'
  },
  'Upload - Excel': (v) => {
    if (isNil(v)) {
      return v
    }
    return encodeURI(v)
  },
  'Upload - File': (v) => {
    if (isNil(v)) {
      return v
    }
    return encodeURI(v)
  },
}

const preSaveConverters = {
  'Upload - Excel': (v) => {
    if (isNil(v)) {
      return v
    }
    return decodeURI(v)
  },
  'Upload - File': (v) => {
    if (isNil(v)) {
      return v
    }
    return decodeURI(v)
  },
}

const getPreSaveData = (data, questions, converters) => {
  const typeLookup = fromPairs(
    map(questions.dealQuestions, (q) => [
      q.serviceProviderQuestionId,
      get(converters, q.questionType, (elem) => elem),
    ])
  )
  return {
    ...data,
    answers: map(data.answers, (a) => ({
      ...a,
      responseText: get(
        typeLookup,
        a.serviceProviderQuestionId,
        (i) => i
      )(a.responseText),
    })),
  }
}

const getConvertedAnswers = (answers, questions, converters) => {
  const typeLookup = fromPairs(
    map(questions, (q) => [
      q.questionId,
      get(converters, q.questionType, (elem) => elem),
    ])
  )

  const convertedAnswers = map(answers, (answer) => ({
    ...answer,
    responseText: get(
      typeLookup,
      answer.serviceProviderQuestionId,
      (i) => i
    )(answer.responseText), // typeLookup[answer.fundQuestionId](answer.responseText),
  }))
  return convertedAnswers
}

const DealsWizardContainer = ({
  onWizardClosed = () => {},
  usage = 'create',
  type = 'deals',
  status = 'draft',
  compId,
  contactId,
  dealId = null,
  dealCategoryName = '',
  refreshDealList,
  openedForView,
  handleViewClose,
  onEditDeal,
}) => {
  const { push } = useHistory()

  const companyId = compId // useSelector(getCompanyId);
  const [questionTypes, setQuestionTypes] = useState([])
  const [currentStep, setCurrentStep] = useState(1)
  const [questions, setQuestions] = useState({})
  const [loading, setLoading] = useState(false)
  const [showDealProfile, setshowDealProfile] = useState(false)
  const [showDealProfileReadOnly, setshowDealProfileReadOnly] = useState(false)

  const parsedDealsAnswers = useDealsParsing(questions)
  const method = usage === 'create' ? 'post' : 'patch'

  const { previewObject, state, dispatch } = parsedDealsAnswers
  const [finished, setFinished] = useState(false)

  const [stepsInErrorId, setStepsInErrorId] = useState([])
  const reduxDispatch = useDispatch()

  const questionsWithOptions = new Set(
    map(
      filter(questionTypes, (qt) => qt.answers),
      (qt) => qt.questionTypeId
    )
  )

  useEffect(
    () => () => {
      reduxDispatch(setPageTitle(null))
    },
    [reduxDispatch]
  )

  useEffect(() => {
    setLoading(true)

    const requests = [
      http.get('/questions/types'),
      http.get(`/${type}/questions`),
    ]

    if (type === 'deals' && usage === 'edit')
      requests.push(http.get(`/deals/answers/${dealId}`))

    axios
      .all(requests)
      .then(
        axios.spread(
          (questionTypesResponse, questionsResponse, answersResponse) => {
            setQuestionTypes(questionTypesResponse.data)
            setQuestions(questionsResponse.data)

            const serviceProviderQuestions = questionsResponse.data

            if (!isNil(answersResponse)) {
              const { serviceProviderName, answers } = answersResponse.data

              dispatch({
                type: types.ADD,
                payload: { serviceProviderName },
              })

              reduxDispatch(setPageTitle(serviceProviderName))

              dispatch({
                type: types.APPEND_ANSWERS,
                payload: getConvertedAnswers(
                  answers,
                  serviceProviderQuestions,
                  converters
                ),
              })
            }
          }
        )
      )
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('[DealWizardContainer]::Error', err)
      })
      .finally(() => {
        setLoading(false)
        if (openedForView) setshowDealProfile(true)
      })
  }, [companyId, dealId, dispatch, reduxDispatch, type, usage])

  useEffect(() => {
    const { ...data } = state
    if (finished) {
      setLoading(true)
      setFinished(false)
      const dealsEndpoint =
        usage === 'create'
          ? `/deals/?publishDeal=false&companyId=${companyId}&contactId=${contactId}`
          : `/deals/${dealId}?publishDeal=false`
      http[method](
        dealsEndpoint,
        getPreSaveData(data, questions, preSaveConverters)
      )
        .then(() => {
          message.success('Deal saved successfully.')
          onWizardClosed()
          reduxDispatch(setPageTitle(null))

          refreshDealList()
        })
        .catch((err) => {
          message.error('Could not save deal.', err)
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }, [
    state,
    push,
    companyId,
    questions,
    reduxDispatch,
    finished,
    usage,
    dealId,
    type,
    method,
    'Deal',
    onWizardClosed,
  ])

  const getStepQuestions = (stepNumber) =>
    filter(questions, (q) => q.stepNumber === stepNumber)

  const getStepAnswers = () => state.answers

  const changeSteps = (step) => {
    const calculatedStep = step + 1
    setCurrentStep(calculatedStep)
  }

  const handleNavigation = (
    questions,
    values,
    actionType,
    isInitial = false,
    isLast = false,
    nextStep = null,
    saveChanges = false,
    publish = false,
    isValid = true
  ) => {
    if (isInitial) {
      dispatch({
        type: types.ADD,
        payload: {
          dealName: values.Name,
        },
      })

      reduxDispatch(setPageTitle(values.Name))
    }

    const answers = dealsAnswers(questions, values, questionsWithOptions)
    const action = {
      type: actionType,
      payload: answers,
    }

    if (publish) {
      setLoading(true)
      if (isValid) {
        const dealsAnswersData = [
          ...filter(
            state.answers,
            (ans) =>
              !includes(
                map(answers, (ans) => ans.questionId),
                ans.questionId
              )
          ),
          ...answers,
        ]

        const answersData = dealsAnswersData
        const url =
          usage === 'create'
            ? `/deals/?publishDeal=true&companyId=${companyId}&contactId=${contactId}`
            : `/deals/${dealId}?publishDeal=true`
        http[method](url, {
          answers: answersData.filter(
            (answer) => answer.responseText !== undefined
          ),
        })
          .then(() => {
            message.success(
              'Success! Your deal has been added and will appear in the search results on the Release date.'
            )
            reduxDispatch(fetchCompanyIfNeeded(companyId))
            setshowDealProfile(false)
            onWizardClosed()
            setLoading(false)

            refreshDealList()
          })
          .catch(() => {
            message.error('Could not publish deal')
            setLoading(false)
          })
      } else {
        setLoading(false)
        message.error('Could not publish deal. Please check highlighted steps.')
      }
    }

    if (saveChanges) {
      if (isValid) {
        action.meta = {
          finished: true,
        }
        dispatch(action)
        setFinished(true)
      } else {
        message.error('Could not save deal. Please check highlighted steps.')
      }
    }

    if (!isEmpty(stepsInErrorId)) {
      validateBeforeSave(answers, [...state.answers])
        .then(() => {
          setStepsInErrorId([])
        })
        .catch((err) => {
          getStepInError(err.inner)
        })
    }

    dispatch(action)

    if (!isLast) {
      if (isNil(nextStep)) {
        // if (isNil(fundId)) {
        //   setCurrentStep(0);
        // }
        setCurrentStep(0)
      } else {
        setCurrentStep(nextStep)
      }
    }
  }

  if (isEmpty(questions)) {
    return (
      <div style={{ marginLeft: '50%' }}>
        <LoadingGif spinning={loading}></LoadingGif>
      </div>
    )
  }
  const allQuestions = [...questions]

  const validateBeforeSave = (answers, allAnswers) => {
    answers.forEach((newAnswer) => {
      const match = allAnswers.find(
        (ans) => ans.shortName === newAnswer.shortName
      )
      const index = allAnswers.indexOf(match)
      allAnswers.splice(index, 1, newAnswer)
    })
    const answersLookup = getInitialDealsValues(
      allQuestions,
      allAnswers,
      questionsWithOptions
    )

    const validator = ServiceProviderValidatorShape(allQuestions)

    return validator.validate(answersLookup, { abortEarly: false })
  }

  const calculateStep = (step) => {
    if (step >= 8) return step - 1
    else return step
  }

  const getStepInError = (errorList) => {
    const errorStepsList = []
    errorList.forEach((error) => {
      const step = allQuestions.find(
        (a) => a.shortName === error.path
      ).stepNumber
      errorStepsList.push(calculateStep(step))
    })
    setStepsInErrorId(errorStepsList)
  }

  return (
    <>
      {!openedForView && (
        <DealWizard
          currentStep={currentStep}
          questions={getStepQuestions(currentStep)}
          companyId={companyId}
          answers={getStepAnswers(currentStep)}
          questionsWithOptions={questionsWithOptions}
          loading={loading}
          onNavigation={handleNavigation}
          onPreview={() => setshowDealProfile(true)}
          onStepChange={changeSteps}
          stepsInErrorId={stepsInErrorId}
          type={type}
          usage={usage}
          status={status.toLowerCase()}
        />
      )}

      {(showDealProfile || showDealProfileReadOnly) && (
        <Modal
          visible={showDealProfile || showDealProfileReadOnly}
          footer={null}
          onCancel={() => {
            setshowDealProfile(false)
            setshowDealProfileReadOnly(false)
            handleViewClose()
          }}
          width="900px"
          centered
        >
          {
            <DealProfileContainer
              deal={previewObject}
              dealCategoryName={dealCategoryName}
              visible={showDealProfile || showDealProfileReadOnly}
              onCancel={() => {
                setshowDealProfile(false)
                setshowDealProfileReadOnly(false)
                handleViewClose()
              }}
              showHeader={true}
              hideActions={true}
              openedForView={openedForView}
              onEditDeal={() => {
                onEditDeal()
              }}
            />
          }
        </Modal>
      )}
    </>
  )
}

DealsWizardContainer.propTypes = {
  onWizardClosed: PropTypes.func,
  usage: PropTypes.string,
  status: PropTypes.string,
  type: PropTypes.string,
  compId: PropTypes.number,
  contactId: PropTypes.number,
  dealId: PropTypes.number,
  openedForView: PropTypes.bool,
}

export default DealsWizardContainer
